import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/auth';

interface Msg { role: 'user' | 'assistant'; content: string }
interface Draft { tipo?: string; prioridad?: string; pantalla?: string; titulo?: string; descripcion?: string; propuesta?: string }
type Item = { kind: 'msg'; m: Msg } | { kind: 'thinking' } | { kind: 'draft'; d: Draft };

export default function VanessaWidget() {
  const { user, isAuthenticated } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [items, setItems] = useState<Item[]>([{ kind: 'msg', m: { role: 'assistant', content: '¡Hola mi\'jo! Soy Vanessa, estoy acá para ayudarte con lo que no funcione en el sistema. Contame qué está pasando, pero contame bien ¿va?' } }]);
  const [avatarOk, setAvatarOk] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Check avatar
  useEffect(() => {
    fetch('/api/vanessa/avatar', { method: 'HEAD' }).then(r => setAvatarOk(r.ok)).catch(() => setAvatarOk(false));
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [items, open]);

  if (!isAuthenticated || !user || user.role !== "admin") return null;

  const historial = (): Msg[] => items.filter(i => i.kind === 'msg').map(i => (i as { kind: 'msg'; m: Msg }).m);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const newItems: Item[] = [...items, { kind: 'msg', m: { role: 'user', content: text } }, { kind: 'thinking' }];
    setItems(newItems);
    setSending(true);
    try {
      const messages = [...historial(), { role: 'user' as const, content: text }];
      const r = await fetch('/api/vanessa/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ messages }) });
      const j = await r.json();
      const noThink = newItems.filter(i => i.kind !== 'thinking');
      const next: Item[] = [...noThink];
      if (j.ok) {
        if (j.reply) next.push({ kind: 'msg', m: { role: 'assistant', content: j.reply } });
        if (j.ticketDraft) next.push({ kind: 'draft', d: j.ticketDraft });
      } else {
        next.push({ kind: 'msg', m: { role: 'assistant', content: 'Diay mi\'jo, algo falló. Decime otra vez.' } });
      }
      setItems(next);
    } catch {
      setItems(prev => [...prev.filter(i => i.kind !== 'thinking'), { kind: 'msg', m: { role: 'assistant', content: 'Se cayó la red mi\'jo. Reintentá.' } }]);
    } finally {
      setSending(false);
    }
  };

  const enviarTicket = async (draft: Draft) => {
    const conversacion = historial();
    try {
      const r = await fetch('/api/vanessa/ticket', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ draft, conversacion, by: user.name || user.email }) });
      const j = await r.json();
      if (j.ok) {
        setSavedToast(true);
        setTimeout(() => setSavedToast(false), 3000);
        setItems(prev => [...prev.filter(i => i.kind !== 'draft'), { kind: 'msg', m: { role: 'assistant', content: 'Listo mi\'jo, se lo mandé a Jose Pablo. Ya te avisará cuando lo resuelva. Y acordate — si esto está mal explicado, me las pagás 😤' } }]);
      }
    } catch { /* ignore */ }
  };

  const seguirHablando = () => setItems(prev => prev.filter(i => i.kind !== 'draft'));

  const esc = (s: string) => s.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Chat con Vanessa"
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
          width: 60, height: 60, borderRadius: '50%',
          background: 'linear-gradient(135deg,#ff00c8,#9000ff)',
          border: 'none', cursor: 'pointer',
          boxShadow: '0 8px 30px rgba(255,0,200,.5), 0 0 0 4px rgba(255,255,255,.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden', transition: 'transform .2s',
          transform: open ? 'scale(0.9)' : 'scale(1)',
        }}
      >
        {avatarOk
          ? <img src="/api/vanessa/avatar" alt="Vanessa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ color: '#fff', fontSize: 24, fontWeight: 900 }}>{open ? '×' : 'V'}</span>}
        {!open && (
          <span style={{ position: 'absolute', top: 2, right: 2, width: 14, height: 14, borderRadius: '50%', background: '#4ade80', border: '2px solid #fff' }} />
        )}
      </button>

      {/* Panel chat */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 90, right: 20, zIndex: 9998,
          width: 'min(400px, calc(100vw - 40px))', height: 'min(620px, calc(100vh - 120px))',
          background: '#0a0a1a', color: '#fff', borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.1)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
        }}>
          {/* Header */}
          <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg,#1a0a2e,#2a0a3e)', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#ff00c8,#9000ff)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {avatarOk ? <img src="/api/vanessa/avatar" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 18, fontWeight: 900 }}>V</span>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 800, background: 'linear-gradient(90deg,#ff00c8,#00e0ff)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Vanessa</div>
              <div style={{ fontSize: 11, opacity: .6 }}><span style={{ color: '#4ade80' }}>●</span> en línea · soporte DTFCR</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Cerrar" style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', opacity: .6, padding: 4 }}>×</button>
          </div>

          {/* Mensajes */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((it, idx) => {
              if (it.kind === 'thinking') {
                return <div key={idx} style={{ alignSelf: 'flex-start', background: 'rgba(255,255,255,.05)', padding: '8px 14px', borderRadius: 14, fontStyle: 'italic', opacity: .6, fontSize: 13 }}>Vanessa está escribiendo…</div>;
              }
              if (it.kind === 'draft') {
                const d = it.d;
                return (
                  <div key={idx} style={{ background: 'linear-gradient(135deg,rgba(255,215,0,.15),rgba(255,0,200,.15))', border: '2px solid rgba(255,215,0,.4)', borderRadius: 14, padding: 14 }}>
                    <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', opacity: .7, marginBottom: 8, fontWeight: 700 }}>📋 Resumen del ticket</div>
                    <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                      <div><b style={{ color: '#ffd700' }}>Tipo:</b> {d.tipo}</div>
                      <div><b style={{ color: '#ffd700' }}>Prioridad:</b> {d.prioridad}</div>
                      <div><b style={{ color: '#ffd700' }}>Pantalla:</b> {d.pantalla}</div>
                      <div style={{ marginTop: 4 }}><b style={{ color: '#ffd700' }}>Título:</b> {d.titulo}</div>
                      <div style={{ marginTop: 4 }}><b style={{ color: '#ffd700' }}>Descripción:</b> {d.descripcion}</div>
                      <div style={{ marginTop: 4 }}><b style={{ color: '#ffd700' }}>Propuesta:</b> {d.propuesta}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                      <button onClick={() => enviarTicket(d)} style={{ flex: 1, padding: '8px 10px', background: 'linear-gradient(90deg,#00c853,#00e676)', color: '#000', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>✅ Enviar</button>
                      <button onClick={seguirHablando} style={{ padding: '8px 10px', background: 'rgba(255,255,255,.15)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✏️ Seguir</button>
                    </div>
                  </div>
                );
              }
              const m = it.m;
              return (
                <div key={idx} style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
                  background: m.role === 'user' ? 'linear-gradient(135deg,#ff00c8,#9000ff)' : 'rgba(255,255,255,.08)',
                  fontSize: 14, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                  borderBottomLeftRadius: m.role === 'assistant' ? 4 : 14,
                  borderBottomRightRadius: m.role === 'user' ? 4 : 14,
                }} dangerouslySetInnerHTML={{ __html: esc(m.content) }} />
              );
            })}
          </div>

          {/* Input */}
          <div style={{ padding: 12, background: 'rgba(0,0,0,.3)', borderTop: '1px solid rgba(255,255,255,.1)', display: 'flex', gap: 8 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Escribí a Vanessa…"
              rows={1}
              style={{ flex: 1, padding: '10px 12px', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.15)', borderRadius: 10, color: '#fff', fontSize: 14, outline: 'none', resize: 'none', fontFamily: 'inherit', maxHeight: 90 }}
            />
            <button onClick={send} disabled={sending || !input.trim()} style={{ background: 'linear-gradient(135deg,#ff00c8,#9000ff)', color: '#fff', border: 'none', borderRadius: 10, padding: '0 16px', fontSize: 18, fontWeight: 900, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? .5 : 1 }}>▶</button>
          </div>
        </div>
      )}

      {/* Toast */}
      {savedToast && (
        <div style={{ position: 'fixed', bottom: 100, right: 20, zIndex: 10000, background: '#00c853', color: '#000', padding: '12px 20px', borderRadius: 999, fontWeight: 800, boxShadow: '0 8px 24px rgba(0,200,80,.4)' }}>
          ✅ Ticket enviado a Jose Pablo
        </div>
      )}
    </>
  );
}
